import type { WikiBlock } from "../types";

/**
 * Selling-critical talent formulas (wiki summary).
 * Authority: docs/architecture/Game_Proto_Judgment_Selling_기질.md
 * Data SSOT: src/data/tables/raid/ai-profiles.json → mapping
 * Polar gates: features/raid/judgment/polar.ts
 * Player-facing: no Soft≪AA / bare AA / DZ jargon (use Soft · 평타 · 장판).
 */
export const talentFormulasBlocks: readonly WikiBlock[] = [
  {
    type: "callout",
    tone: "info",
    text: "플레이에서 “슬라이더만 보고 행동이 예측되는” 식만 모았습니다. 상수 전부·모듈 경로는 호스트 Judgment Selling 정본 · 테이블 ai-profiles.json mapping.",
  },
  {
    type: "callout",
    tone: "warning",
    text: "발밑 Soft 위험 원만 비웠다고 평타 밖까지 도망친 척하면 안 됩니다. Soft 생존 버프(이속 등)는 Soft 바닥이지 재능 슬라이더가 아닙니다.",
  },
  { type: "h2", text: "공통" },
  {
    type: "p",
    text: "축 값 0~100 → 중심 정규화 t = (축 − 50) / 50 ∈ [−1, +1]. 선형 맵: 값 = clamp(base + slope·t, min, max).",
  },
  {
    type: "ul",
    items: [
      "양극(polar): ≤20 = 낮음 · ≥80 = 높음 · 그 사이 = 중간 — 극단 tradeoff 게이트",
      "통찰 런타임: insight = 0.5·measure + 0.5·selfProtect (로스터/Sim Lab은 둘을 insight로 대칭)",
      "테이블: tables/raid/ai-profiles.json · polar: judgment/polar.ts",
    ],
  },
  { type: "h2", text: "직감 — 언제·얼마나 피하나" },
  {
    type: "table",
    headers: ["식 / 게이트", "0 · 50 · 100 (대략)", "무엇을 파는지"],
    rows: [
      [
        "reactionDelaySec = clamp(0.5 − 0.5·t, 0, 1)",
        "1.0s · 0.5s · 0s",
        "위험 인지→급피 개방까지 딜레이",
      ],
      [
        "dodgeDistMul = clamp(1.05 + 0.5·t, 0.55, 1.55)",
        "0.55 · 1.05 · 1.55",
        "급피 이탈 거리(거리만 · 이속 아님)",
      ],
      [
        "직감 ≤20 → 장판/텔레그래프 급피·자리 깨기 닫힘",
        "열림은 >20",
        "바닥 직감 = 맞고 싸움(급피 시도 없음)",
      ],
      [
        "직감 ≥80 → 급피 중 기회사격·힐 포기",
        "중간 이하는 발 밖이면 유지",
        "높이면 몸이 살고 손을 놓침",
      ],
    ],
  },
  { type: "h2", text: "통찰 — 어디에 서나 · 얼마나 망설이나" },
  {
    type: "table",
    headers: ["식 / 게이트", "0 · 50 · 100 (대략)", "무엇을 파는지"],
    rows: [
      [
        "standRangeMul = clamp(0.82 + 0.6·t, 0.18, 1.45)",
        "가깝게 · 중간 · 멀리",
        "스탠드/카이트 거리감",
      ],
      [
        "fleeDistMul = clamp(1.06 + 0.45·t, 0.5, 1.65)",
        "짧게 · 중간 · 길게",
        "빠질 때 거리 배수",
      ],
      [
        "평타 여유 pad(px) = clamp(14 + 18·t, 0, 48)",
        "0 · 14 · 48",
        "위험·평타 경계에서 여유 칸",
      ],
      [
        "hesitateMul = 1 + max(0,t)·0.85",
        "1 · 1 · 1.85",
        "표적 전환 망설임(높을수록 느림)",
      ],
      [
        "생존 자리 tip — Soft clear 좌석만 · 직감>20일 때",
        "직감≤20이면 tip 0",
        "안전한 칸 안에서 힐 닿는 곳·탱 뒤 선호",
      ],
    ],
  },
  { type: "h2", text: "유대 — 부관 제안을 얼마나 듣나" },
  {
    type: "table",
    headers: ["식 / 게이트", "0 · 50 · 100 (대략)", "무엇을 파는지"],
    rows: [
      [
        "adjutantFollowMul = clamp(1 + t, 0, 1.6)",
        "0 · 1 · 1.6",
        "부관 자리 시드 추종 강도",
      ],
      [
        "준수도 ≈ follow / 1.6",
        "0 · ≈0.63 · 1",
        "0=무시 · 1=완전 따름",
      ],
      [
        "healSplitMul = clamp(1 + t, 0, 1)",
        "0 · 1 · 1",
        "힐 분산(낮으면 최저 HP만 몰아줌)",
      ],
      [
        "과준수 pull ≤0.95 (급피 keep-clear 시 시드 혼합)",
        "0 · ≈0.44 · 0.95",
        "너무 높으면 나쁜 콜도 따름",
      ],
    ],
  },
  { type: "h2", text: "인내 — 판매 제외 · 역할 바닥" },
  {
    type: "p",
    text: "surviveHpThr = clamp(0.5 − 0.5·t, 0, 1) → 인내 0≈피 100%부터 생존 후퇴 · 50≈50% · 100≈후퇴 안 함(thr=0). 직업 기본 버티기만 — 직감/통찰/유대와 섞지 않음.",
  },
  { type: "h2", text: "일부러 안 적은 것" },
  {
    type: "ul",
    items: [
      "Soft 스택 생존 이속(배율) — Soft 바닥 · 직감이 아님",
      "stickyGain · personalSticky — 유대↓일수록↑지만 판매 라벨 아님",
      "구조(rescue) 세부 knob · classBase/quirk 직업 기본값 표 전체",
      "plant/kit Layer2 등 배선 off·비핵심 상수",
    ],
  },
  {
    type: "p",
    text: "축별 플레이 카피: Wiki 「직감」·「통찰」·「유대」·「인내」. 방법론: 「똑똑한 영웅을 만드는 법」. 정본 식: Judgment Selling 기질.",
  },
];
